import type { ContactRequestMediaAttachmentInput } from '~/shared/api'
import { MediaDomainNameEnum, useGetMediaUploadUrlLazyQuery } from '~/shared/api'
import { delay } from '~/shared/lib'
import { getUniqId } from '~/shared/lib/text/uniq'

import {
  CONTACT_REQUEST_MEDIA_BUCKET,
  CONTACT_REQUEST_MEDIA_ENTITY_TYPE,
} from './const'

async function putFileToPresignedUrl(file: File, url: string): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  })

  if (!response.ok) {
    throw new Error(`Ошибка загрузки: ${response.statusText}`)
  }

  await delay(2000)
}

async function getMediaUploadUrl(
  load: ReturnType<typeof useGetMediaUploadUrlLazyQuery>['load'],
  refetch: ReturnType<typeof useGetMediaUploadUrlLazyQuery>['refetch'],
  input: {
    mediaEntityId: string
    mediaEntityType: string
    mediaDomain: MediaDomainNameEnum
    filename: string
  },
) {
  const newMediaResult = await refetch({ input })

  if (newMediaResult?.data?.getMediaUploadUrl) {
    return newMediaResult.data.getMediaUploadUrl
  }

  const loadResult = await load(null, { input })

  if (loadResult === false) {
    return null
  }

  return loadResult.getMediaUploadUrl ?? null
}

function normalizeFilename(file: File, index: number): string {
  const raw = file.name?.trim()
  if (raw) {
    return raw
  }
  return `attachment-${index}-${getUniqId()}`
}

export function useContactRequestMediaUpload() {
  const { load, refetch } = useGetMediaUploadUrlLazyQuery(undefined, { fetchPolicy: 'no-cache' })

  async function uploadContactRequestAttachments(
    files: File[],
    mediaEntityId: string,
  ): Promise<ContactRequestMediaAttachmentInput[]> {
    const attachments: ContactRequestMediaAttachmentInput[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const filename = normalizeFilename(file, i)

      const media = await getMediaUploadUrl(load, refetch, {
        mediaEntityId,
        mediaDomain: MediaDomainNameEnum.MsMail,
        mediaEntityType: CONTACT_REQUEST_MEDIA_ENTITY_TYPE,
        filename,
      })

      if (!media?.mediaUploadUrl || !media.mediaStorageKey) {
        throw new Error('Не удалось получить ссылку для загрузки файла')
      }

      await putFileToPresignedUrl(file, media.mediaUploadUrl)

      attachments.push({
        bucket: CONTACT_REQUEST_MEDIA_BUCKET,
        objectKey: media.mediaStorageKey,
      })
    }

    return attachments
  }

  return {
    uploadContactRequestAttachments,
  }
}
